import type { CampanaCrearInput, InstanciaPublic } from '@app/shared';
import { TIPOS_CAMPANA_ATRIBUCION } from '@app/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorCode, getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { campanasApi } from '../api/campanas.api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instancias: InstanciaPublic[];
}

// La campaña de envío (EMISION) no se arma acá: tiene su propia pantalla (ciclo-emisor).
type TipoCampanaAtribucion = (typeof TIPOS_CAMPANA_ATRIBUCION)[number];

const hoy = (): string => new Date().toISOString().slice(0, 10);

// Alta de una campaña (Ciclo 1c R5). Los textos se separan con una línea en blanco; el backend valida
// el resto. Para Meta Ads el texto es el mensaje prefijado del anuncio (lo escribe Ailen).
export function CampanaCrearDialog({ open, onOpenChange, instancias }: Props): JSX.Element {
  const es = useI18n();
  const queryClient = useQueryClient();
  const t = es.admin.campanas.dialogo;
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<TipoCampanaAtribucion>('META_ADS');
  const [elegidas, setElegidas] = useState<string[]>([]);
  const [textos, setTextos] = useState('');
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: campanasApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.campanas.all });
      toast.success(t.exito);
      cerrar();
    },
    onError: (e) => {
      const code = getErrorCode(e);
      setError(
        code === 'NOMBRE_EN_USO' ? t.errorNombreEnUso : getErrorMessage(e, es.errores.generico.algoSalioMal),
      );
    },
  });

  function cerrar(): void {
    setNombre('');
    setTipo('META_ADS');
    setElegidas([]);
    setTextos('');
    setDesde(hoy());
    setHasta('');
    setError(null);
    onOpenChange(false);
  }

  function enviar(): void {
    setError(null);
    if (nombre.trim().length < 2) {
      setError(t.errorNombre);
      return;
    }
    if (elegidas.length === 0) {
      setError(t.errorNumeros);
      return;
    }
    const input: CampanaCrearInput = {
      nombre: nombre.trim(),
      tipo,
      instancias: elegidas,
      textos: textos
        .split(/\n\s*\n/)
        .map((s) => s.trim())
        .filter((s) => s.length >= 3),
      vigenteDesde: new Date(`${desde}T00:00:00-03:00`),
      vigenteHasta: hasta ? new Date(`${hasta}T23:59:59-03:00`) : null,
      activa: true,
    };
    mutation.mutate(input);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : cerrar())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.titulo}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          {error && <Alert variant="error">{error}</Alert>}

          <FormField label={t.nombre} htmlFor="campana-nombre">
            <Input id="campana-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </FormField>

          <FormField label={t.tipo} htmlFor="campana-tipo">
            <select
              id="campana-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoCampanaAtribucion)}
              className="h-10 rounded-md border border-marca-neutro bg-surface px-3 text-sm text-marca-gris"
            >
              {TIPOS_CAMPANA_ATRIBUCION.map((v) => (
                <option key={v} value={v}>
                  {es.admin.campanas.tipos[v]}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label={t.numeros} htmlFor="campana-numeros">
            <div id="campana-numeros" className="flex flex-wrap gap-3">
              {instancias.map((i) => (
                <label key={i.nombre} className="flex items-center gap-1.5 text-sm text-text-strong">
                  <input
                    type="checkbox"
                    checked={elegidas.includes(i.nombre)}
                    onChange={(e) =>
                      setElegidas((prev) =>
                        e.target.checked ? [...prev, i.nombre] : prev.filter((n) => n !== i.nombre),
                      )
                    }
                    className="h-4 w-4 accent-marca-primario"
                  />
                  {i.etiqueta} · {i.nombre}
                </label>
              ))}
            </div>
          </FormField>

          <FormField label={t.textos} htmlFor="campana-textos" helper={t.textosAyuda}>
            <Textarea
              id="campana-textos"
              rows={5}
              value={textos}
              onChange={(e) => setTextos(e.target.value)}
              className="text-sm"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t.vigenteDesde} htmlFor="campana-desde">
              <Input
                id="campana-desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </FormField>
            <FormField label={t.vigenteHasta} htmlFor="campana-hasta">
              <Input
                id="campana-hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrar}>
              {t.cancelar}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t.creando : t.crear}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
